import { useEffect, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { type MentionProvider } from '@atlaskit/mention/resource';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';

import {
	AgentAwareMentionProvider,
	type SelectedAgentRef,
	withAgentSupport,
} from '../services/rovo-mention-resource/with-agent-support';

/**
 * Default URS people-search filter: active users only, no Connect bots, no
 * apps. Used when agent mentions are disabled.
 */
const ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER =
	'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)';

/**
 * URS search filter used by the Rovo Chat agent-mention flow. Returns
 * humans and Rovo agents (apps tagged with `app_type:"agent"`) in a single
 * query, excluding system users, deactivated accounts, legacy Connect bots,
 * and non-agent apps (Forge, service accounts, etc.).
 *
 * Clauses:
 * - `account_status:"active"` — exclude deactivated/suspended accounts.
 * - `NOT not_mentionable:true` — exclude system users (e.g. the `Jira`
 *   bot duplicates) that should never appear in an @-mention picker.
 * - The OR group has two branches that together describe the allow-list:
 *   - Branch 1 (humans): `NOT account_type:"app"` paired with
 *     `NOT email_domain:"connect.atlassian.com"` to keep all non-app
 *     accounts while still excluding legacy Connect bots that masquerade
 *     as humans via that email domain.
 *   - Branch 2 (agents): `account_type:"app" AND app_type:"agent"`
 *     positively matches Rovo agents only, leaving Forge / service apps
 *     out.
 */
const ACTIVE_USER_AND_APP_SEARCH_FILTER =
	'account_status:"active" AND (NOT not_mentionable:true) AND ((NOT account_type:"app" AND (NOT email_domain:"connect.atlassian.com")) OR (account_type:"app" AND app_type:"agent"))';

type UseChatMentionResourceOptions = {
	productKey: string;
	siteId: string;
	orgId: string | undefined;
	currentUserId: string | undefined;
	/**
	 * When provided, takes precedence over the constructed `SmartMentionResource`
	 * and any agent decoration. Used by consumers that already own a mention
	 * provider (e.g. external embeds, tests).
	 */
	mentionResourceOverride: MentionProvider | undefined;
	/**
	 * When `true`, the provider is decorated with agent-merge behaviour:
	 * recommended agents are prepended to empty-query results, and URS agent
	 * rows are surfaced inline with people on typed queries.
	 */
	enableAgents: boolean;
	/**
	 * Getter for the latest recommended-agents list. Function form (read
	 * from a ref) so the underlying provider always sees the freshest value
	 * without rebuilding when the list mutates.
	 */
	getRecommendedAgents: () => readonly Agent[];
	/**
	 * Fired when the user selects an agent row from the typeahead. Function
	 * form (read from a ref) so the handler can be swapped without rebuilding.
	 */
	onAgentSelected: (agent: SelectedAgentRef) => void;
};

/**
 * Owns the lifecycle of the @-mention provider used by Rovo Chat input
 * surfaces. Constructs a `SmartMentionResource` (or honours an override),
 * optionally decorates it with `AgentAwareMentionProvider`, and disposes
 * the decorator when the provider is rebuilt or the component unmounts.
 *
 * Consumers receive a `Promise<MentionProvider>` matching what the editor
 * expects to be passed to `mentions: { provider }`.
 *
 * All cleanup happens internally — callers do not need their own `useEffect`
 * to manage subscription lifetimes on the inner provider.
 */
export const useChatMentionResource = ({
	productKey,
	siteId,
	orgId,
	currentUserId,
	mentionResourceOverride,
	enableAgents,
	getRecommendedAgents,
	onAgentSelected,
}: UseChatMentionResourceOptions): Promise<MentionProvider> => {
	di(SmartMentionResource);

	// Construction is memoised so first paint has a real provider and consumers
	// (e.g. the editor) see a stable reference between renders. The `provider`
	// returned here may or may not own disposable resources — only the
	// `AgentAwareMentionProvider` does, and the effect below handles its
	// cleanup. When the gate is off there is nothing to dispose: the bare
	// `SmartMentionResource` cleans up via its own lifecycle.
	const provider = useMemo<MentionProvider>(() => {
		if (mentionResourceOverride) {
			return mentionResourceOverride;
		}

		const innerProvider = new SmartMentionResource({
			productKey,
			siteId,
			includeTeams: true,
			includeUsers: true,
			orgId,
			searchQueryFilter: enableAgents
				? ACTIVE_USER_AND_APP_SEARCH_FILTER
				: ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER,
			productAttributes: enableAgents ? { client: 'rovo-chat' } : undefined,
			shouldHighlightMention: (mention) => mention.id === currentUserId,
		});

		if (!enableAgents) {
			return innerProvider;
		}

		return withAgentSupport(innerProvider, {
			getRecommendedAgents,
			onAgentSelected,
		});
		// getRecommendedAgents and onAgentSelected are deliberately stable refs
		// from the consumer side; the wrapper reads them lazily on each emit so
		// they do not need to be deps here.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productKey, siteId, orgId, currentUserId, mentionResourceOverride, enableAgents]);

	// Whenever the memoised provider is replaced (deps change) or the
	// component unmounts, dispose the previous instance if it owns cleanup.
	// `AgentAwareMentionProvider` is the only kind we construct that does.
	useEffect(() => {
		if (!(provider instanceof AgentAwareMentionProvider)) {
			return;
		}
		return () => provider.dispose();
	}, [provider]);

	// The editor's `mentions: { provider }` accepts a Promise.
	return useMemo(() => Promise.resolve(provider), [provider]);
};
